import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import PlaceAutocomplete from './components/PlaceAutocomplete';
import LiveMap from './components/Map';
import RouteExplorer from './components/RouteExplorer';
import './App.css';
import './Timeline.css';
import FeedbackButton from './components/FeedbackButton';
import MinimalBusSchedule from './components/MinimalBusSchedule';

const resolvePlaceCoordinates = async (place) => {
  if (!place || !place.name) {
    return place;
  }

  // If this place was selected from dropdown, use it as-is
  if (place.fromAutocomplete === true && place.coordinates) {
    return place;
  }

  if (!(window.google && window.google.maps && window.google.maps.places)) {
    return place;
  }

  const autocompleteService = new window.google.maps.places.AutocompleteService();

  // Atlanta bounds
  const atlantaBounds = new window.google.maps.LatLngBounds(
    new window.google.maps.LatLng(33.6, -84.5),  // Southwest
    new window.google.maps.LatLng(33.9, -84.3)    // Northeast
  );

  const predictions = await new Promise((resolve) => {
    autocompleteService.getPlacePredictions(
      {
        input: place.name,
        bounds: atlantaBounds,
        componentRestrictions: { country: 'us' }
      },
      (suggestions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          suggestions &&
          suggestions.length > 0
        ) {
          resolve(suggestions);
        } else {
          resolve([]);
        }
      }
    );
  });

  if (!predictions.length) {
    return place;
  }

  const topPrediction = predictions[0];
  const placesService = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  const details = await new Promise((resolve) => {
    placesService.getDetails(
      {
        placeId: topPrediction.place_id,
        fields: ['geometry', 'name', 'formatted_address', 'types']
      },
      (result, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          result &&
          result.geometry &&
          result.geometry.location
        ) {
          resolve(result);
        } else {
          resolve(null);
        }
      }
    );
  });

  if (!details) {
    return place;
  }

  return {
    name: details.name || place.name,
    address: details.formatted_address,
    coordinates: `${details.geometry.location.lat()},${details.geometry.location.lng()}`,
    placeId: topPrediction.place_id,
    types: details.types,
    fromAutocomplete: true
  };
};

function App() {
  const [currentView, setCurrentView] = useState('search'); // 'search', 'map', or 'stop-times'
  const [beginPlace, setBeginPlace] = useState({ name: '', coordinates: '', fromAutocomplete: false });
  const [destPlace, setDestPlace] = useState({ name: '', coordinates: '', fromAutocomplete: false });
  const beginAutocompleteRef = useRef(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  // Removed blocking isInitializing state - data warms up in background
  const [error, setError] = useState('')
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [disclaimer, setDisclaimer] = useState('');
  const searchInFlightRef = useRef(false);
  /** Drop back-to-back identical searches (double submit / picker quirks). */
  const lastRouteSearchDedupeRef = useRef({ key: '', t: 0 });
  const searchTokenRef = useRef(0);
  const slowerRouteIdsRef = useRef(new Set());
  const slowerGoogleFetchedRef = useRef(false);

  // Initialize data on mount (warm up backend cache in background)
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Determine API URL based on environment
        const apiUrl = (() => {
          const hostname = window.location.hostname;
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return '/api/RouteSearch/';
          }
          return 'https://buzzbus-production.up.railway.app/api/RouteSearch/';
        })();

        // Fire and forget - don't wait or block UI
        console.log("Warming up backend cache...");
        axios.get(`${apiUrl}init-data`).then(() => {
          console.log("Backend data cached and ready.");
        }).catch(err => {
          console.warn("Background cache warmup failed:", err);
        });

      } catch (err) {
        console.warn("Init logic failed", err);
      }
    };

    initializeData();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (searchInFlightRef.current) return;
    if (!beginPlace.name || !destPlace.name) {
      setError('Please select both starting and destination locations');
      return;
    }

    searchInFlightRef.current = true;
    searchTokenRef.current += 1;
    const activeSearchToken = searchTokenRef.current;
    slowerRouteIdsRef.current = new Set();
    slowerGoogleFetchedRef.current = false;
    setLoading(true);
    setError('');
    setRoutes([]);
    setDisclaimer('');

    try {
      // Only resolve if not already selected from dropdown
      const resolvedBegin = beginPlace.fromAutocomplete === true && beginPlace.coordinates
        ? beginPlace
        : await resolvePlaceCoordinates(beginPlace);

      const resolvedDest = destPlace.fromAutocomplete === true && destPlace.coordinates
        ? destPlace
        : await resolvePlaceCoordinates(destPlace);

      if (!resolvedBegin?.coordinates || !resolvedDest?.coordinates) {
        setError('Please pick locations from the suggestions so we can use exact coordinates.');
        setLoading(false);
        searchInFlightRef.current = false;
        return;
      }

      // Keep displayed text in sync with the resolved place names
      setBeginPlace(resolvedBegin);
      setDestPlace(resolvedDest);

      const requestData = {
        begin_location: resolvedBegin.name,
        dest_location: resolvedDest.name,
        begin_coordinates: resolvedBegin.coordinates,
        dest_coordinates: resolvedDest.coordinates
      };

      const dedupeKey = `${requestData.begin_coordinates}\x1f${requestData.dest_coordinates}\x1f${requestData.begin_location}\x1f${requestData.dest_location}`;
      const dedupeNow = Date.now();
      if (
        dedupeKey === lastRouteSearchDedupeRef.current.key &&
        dedupeNow - lastRouteSearchDedupeRef.current.t < 800
      ) {
        setLoading(false);
        searchInFlightRef.current = false;
        return;
      }
      lastRouteSearchDedupeRef.current = { key: dedupeKey, t: dedupeNow };

      // Determine API URL based on environment
      const { apiUrl, shouldSkipAnalytics } = (() => {
        const hostname = window.location.hostname;
        // Local development - use proxy
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return { apiUrl: '/api/RouteSearch/', shouldSkipAnalytics: true };
        }
        // Production - use Railway backend
        return {
          apiUrl: 'https://buzzbus-production.up.railway.app/api/RouteSearch/',
          shouldSkipAnalytics: false
        };
      })();

      const headers = shouldSkipAnalytics ? { 'X-RideGT-Analytics': 'skip' } : undefined;
      const postSearch = (params) => axios.post(apiUrl, requestData, { params, headers });

      const mergeRoutes = (incomingRoutes) => {
        if (!incomingRoutes || incomingRoutes.length === 0) return;
        setRoutes((prevRoutes) => {
          const mergedById = new Map(prevRoutes.map((r) => [r.routeId, r]));
          incomingRoutes.forEach((route) => {
            if (route?.routeId) mergedById.set(route.routeId, route);
          });

          const fastest = [];
          const slower = [];
          for (const route of mergedById.values()) {
            if (slowerRouteIdsRef.current.has(route.routeId)) {
              slower.push(route);
            } else {
              fastest.push(route);
            }
          }
          return [...fastest, ...slower];
        });
      };

      const fastestHeuristicPromise = postSearch({
        rank_offset: 0,
        rank_limit: 1,
        include_google_walking: false
      });
      const fastestGooglePromise = postSearch({
        rank_offset: 0,
        rank_limit: 1,
        include_google_walking: true
      });
      const slowerHeuristicPromise = postSearch({
        rank_offset: 1,
        rank_limit: 2,
        include_google_walking: false
      });

      fastestHeuristicPromise
        .then((response) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          const fastestRoutes = response.data?.routes || [];
          mergeRoutes(fastestRoutes);
          setDisclaimer(response.data?.disclaimer || '');
          if (fastestRoutes.length > 0) {
            setError('');
          } else if (response.data?.message) {
            setError(response.data.message);
          }
          setLoading(false);
        })
        .catch((err) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          console.error('Fastest heuristic route request failed:', err);
        });

      fastestGooglePromise
        .then((response) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          mergeRoutes(response.data?.routes || []);
        })
        .catch((err) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          console.warn('Fastest Google walking enrichment failed:', err);
        });

      slowerHeuristicPromise
        .then((response) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          const slowerRoutes = response.data?.routes || [];
          slowerRouteIdsRef.current = new Set(slowerRoutes.map((r) => r.routeId).filter(Boolean));
          mergeRoutes(slowerRoutes);
        })
        .catch((err) => {
          if (searchTokenRef.current !== activeSearchToken) return;
          console.warn('Slower heuristic routes request failed:', err);
        });

      const settled = await Promise.allSettled([
        fastestHeuristicPromise,
        fastestGooglePromise,
        slowerHeuristicPromise
      ]);
      if (searchTokenRef.current !== activeSearchToken) return;

      const hasAnyRoutes = routes.length > 0 || settled.some(
        (item) => item.status === 'fulfilled' && (item.value?.data?.routes || []).length > 0
      );
      if (!hasAnyRoutes) {
        setError('No routes found between these locations.');
      }
    } catch (err) {
      setError('Failed to search routes. Please try again.');
      console.error('Error searching routes:', err);
    } finally {
      setLoading(false);
      searchInFlightRef.current = false;
    }
  };

  const handleBeginPlaceSelect = useCallback((placeData) => {
    setBeginPlace({ ...placeData, fromAutocomplete: true });
  }, []);

  const handleDestPlaceSelect = useCallback((placeData) => {
    setDestPlace({ ...placeData, fromAutocomplete: true });
  }, []);

  const handleBeginPlaceChange = useCallback((e) => {
    setBeginPlace({ name: e.target.value, coordinates: '', fromAutocomplete: false });
  }, []);

  const handleDestPlaceChange = useCallback((e) => {
    setDestPlace({ name: e.target.value, coordinates: '', fromAutocomplete: false });
  }, []);

  const handleUseCurrentLocation = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        // Log coordinates to console for verification
        console.log('📍 Current Location Captured:');
        console.log(`Latitude: ${latitude}`);
        console.log(`Longitude: ${longitude}`);
        console.log(`Accuracy: ${accuracy} meters`);
        console.log('Full position object:', position);

        setBeginPlace({
          name: 'Current Location',
          coordinates: `${latitude},${longitude}`,
          fromAutocomplete: true
        });

        // Close the autocomplete dropdown if it's open
        if (beginAutocompleteRef.current) {
          beginAutocompleteRef.current.closeSuggestions();
        }

        setLoading(false);
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError('Location permission denied. Please enable location access in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('Location information is unavailable. Please input your starting location manually.');
            break;
          case error.TIMEOUT:
            setError('Location request timed out.');
            break;
          default:
            setError('An unknown error occurred while getting your location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, []);

  const handleClearBeginPlace = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBeginPlace({ name: '', coordinates: '', fromAutocomplete: false });
  }, []);

  const handleClearDestPlace = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setDestPlace({ name: '', coordinates: '', fromAutocomplete: false });
  }, []);

  const handleRouteClick = useCallback((routeId) => {
    // Toggle selection: if clicking the same route, deselect it; otherwise select the new one
    setSelectedRouteId(prevId => prevId === routeId ? null : routeId);

    if (!slowerRouteIdsRef.current.has(routeId) || slowerGoogleFetchedRef.current) {
      return;
    }

    const requestData = {
      begin_location: beginPlace.name,
      dest_location: destPlace.name,
      begin_coordinates: beginPlace.coordinates,
      dest_coordinates: destPlace.coordinates
    };
    if (!requestData.begin_coordinates || !requestData.dest_coordinates) {
      return;
    }

    const { apiUrl, shouldSkipAnalytics } = (() => {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return { apiUrl: '/api/RouteSearch/', shouldSkipAnalytics: true };
      }
      return {
        apiUrl: 'https://buzzbus-production.up.railway.app/api/RouteSearch/',
        shouldSkipAnalytics: false
      };
    })();

    slowerGoogleFetchedRef.current = true;
    axios.post(apiUrl, requestData, {
      params: {
        rank_offset: 1,
        rank_limit: 2,
        include_google_walking: true
      },
      headers: shouldSkipAnalytics ? { 'X-RideGT-Analytics': 'skip' } : undefined
    }).then((response) => {
      setRoutes((prevRoutes) => {
        const byId = new Map(prevRoutes.map((r) => [r.routeId, r]));
        (response.data?.routes || []).forEach((route) => {
          if (route?.routeId) byId.set(route.routeId, route);
        });
        const fastest = [];
        const slower = [];
        for (const route of byId.values()) {
          if (slowerRouteIdsRef.current.has(route.routeId)) slower.push(route);
          else fastest.push(route);
        }
        return [...fastest, ...slower];
      });
    }).catch((err) => {
      slowerGoogleFetchedRef.current = false;
      console.warn('Slower Google walking enrichment failed:', err);
    });
  }, [beginPlace, destPlace]);

  const renderSuggestions = useCallback(({ predictions, onSelect }) => (
    <div style={{
      position: 'absolute',
      zIndex: 1000,
      backgroundColor: 'white',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      borderRadius: '0.5rem',
      marginTop: '0.75rem',
      width: '100%',
      border: '1px solid #B3A369',
      overflow: 'hidden'
    }}>
      {predictions.map((prediction, index) => (
        <div
          key={
            prediction.place_id
            || prediction.placeId
            || (prediction.campusQuickPick ? `campus-${prediction.name}` : null)
            || (prediction.recentLocation ? `recent-${prediction.name}-${index}` : null)
            || prediction.name
            || index
          }
          onClick={() => {
            if (prediction.recentLocation) {
              // Pass the object as-is, not a new object
              onSelect(prediction);
            } else {
              onSelect(prediction);
            }
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FFF9C4'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: index === predictions.length - 1 ? 'none' : '1px solid #f1f5f9',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            color: '#003057',
            transition: 'background-color 0.15s ease'
          }}
        >
          <span style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {prediction.description || prediction.name}
          </span>
          {prediction.recentLocation && (
            <span style={{
              marginLeft: 'auto',
              paddingLeft: 8,
              fontSize: '12px',
              color: '#B3A369',
              fontStyle: 'italic',
              flexShrink: 0,
              textAlign: 'right'
            }}>
              Recent
            </span>
          )}
        </div>
      ))}
    </div>
  ), []);

  return (
    <div className="App">
      <Analytics />
      <SpeedInsights />

      {/* Header with GT stripe */}
      <div className="header-container">
        <div className="header-content">
          <div className="header-branding">
            <h1 className="header-title">RideGT</h1>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="nav-container">
        <div className="nav-content">
          <button
            onClick={() => setCurrentView('search')}
            className={`nav-tab ${currentView === 'search' ? 'active' : ''}`}
          >
            Route Finder
          </button>
          <button
            onClick={() => setCurrentView('stop-times')}
            className={`nav-tab ${currentView === 'stop-times' ? 'active' : ''}`}
          >
            Arrivals
          </button>
          <button
            onClick={() => setCurrentView('map')}
            className={`nav-tab ${currentView === 'map' ? 'active' : ''}`}
          >
            Live Map
          </button>

        </div>
      </div>

      <div style={{ display: currentView === 'map' ? 'block' : 'none', height: '100%' }}>
        <LiveMap isVisible={currentView === 'map'} />
      </div>

      <div style={{ display: currentView === 'stop-times' ? 'block' : 'none', height: '100%' }}>
        <RouteExplorer />
      </div>

      <div style={{ display: currentView === 'search' ? 'block' : 'none', height: '100%' }}>
        <main className="App-main">
          <form onSubmit={handleSearch} className="search-form">
            <div className="form-inputs">
              <div className="input-wrapper from-input">
                <div className="input-accent"></div>
                <PlaceAutocomplete
                  ref={beginAutocompleteRef}
                  id="begin-place"
                  placeholder="From"
                  value={beginPlace.name}
                  onChange={handleBeginPlaceChange}
                  onPlaceSelect={handleBeginPlaceSelect}
                  className="route-input with-action"
                  renderSuggestions={renderSuggestions}
                />
                {beginPlace.name ? (
                  <button
                    type="button"
                    onClick={handleClearBeginPlace}
                    disabled={loading}
                    className="clear-location-btn"
                    title="Clear starting location"
                    aria-label="Clear starting location"
                  >
                    ×
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    disabled={loading}
                    className="current-location-btn"
                    title="Use current location"
                    aria-label="Use current location"
                  >
                    📍
                  </button>
                )}
              </div>

              <div className="input-wrapper to-input">
                <div className="input-accent dark"></div>
                <PlaceAutocomplete
                  id="dest-place"
                  placeholder="To"
                  value={destPlace.name}
                  onChange={handleDestPlaceChange}
                  onPlaceSelect={handleDestPlaceSelect}
                  className={`route-input ${destPlace.name ? 'with-action' : ''}`}
                  renderSuggestions={renderSuggestions}
                />
                {destPlace.name ? (
                  <button
                    type="button"
                    onClick={handleClearDestPlace}
                    disabled={loading}
                    className="clear-location-btn"
                    title="Clear destination"
                    aria-label="Clear destination"
                  >
                    ×
                  </button>
                ) : null}
              </div>

              <button type="submit" disabled={loading} className="find-route-button">
                {loading ? 'Searching...' : 'Find Route'}
              </button>
            </div>
          </form>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {routes.length > 0 && (
            <div className="results-section">
              {disclaimer && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  fontStyle: 'italic',
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0.375rem',
                  border: '1px solid #e2e8f0'
                }}>
                  {disclaimer}
                </div>
              )}
              <div className="results-header">AVAILABLE ROUTES</div>

              <div className="routes-container">
                {/* Filter routes: show only selected route if one is selected, otherwise show all */}
                {(selectedRouteId ? routes.filter(r => r.routeId === selectedRouteId) : routes).map((route, index) => (
                  <MinimalBusSchedule
                    key={route.routeId}
                    route={route}
                    onToggle={() => handleRouteClick(route.routeId)}
                    isExpanded={selectedRouteId === route.routeId}
                    beginPlace={beginPlace}
                    destPlace={destPlace}
                  />
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
      <FeedbackButton />

      {/* Disclaimer Footer - sticks to bottom of viewport on mobile */}
      <footer style={{
        padding: '1rem',
        textAlign: 'center',
        fontSize: '0.75rem',
        color: '#64748b',
        borderTop: '1px solid #e2e8f0',
        marginTop: 'auto',
        backgroundColor: '#f8fafc',
        flexShrink: 0
      }}>
        This app is not affiliated with, endorsed by, or representative of the Georgia Institute of Technology.
        <br />
        Developed by Vedant Bhatt &#38; Rohan Nair.
        <br />
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#64748b', textDecoration: 'underline', fontSize: '0.75rem' }}
        >
          Privacy Policy
        </a>
        {' · '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#64748b', textDecoration: 'underline', fontSize: '0.75rem' }}
        >
          Terms &amp; Conditions
        </a>
      </footer>
    </div>
  );
}

export default App;
